import { Component, OnInit } from '@angular/core';
import { UserRolesService, UserListDto, UserFilterDto, PagedResult, CreateUserDto, UpdateUserDto, UserWithRolesDto } from '../../../../services/user-roles.service';
import { RolesService, RoleDto } from '../../../../services/roles.service';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss']
})
export class UsersListComponent implements OnInit {
  users: UserListDto[] = [];
  allRoles: RoleDto[] = [];
  loading = false;
  
  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  // Filtros
  searchQuery = '';
  selectedRoleFilter = '';
  
  // Modal de asignación de roles
  showRoleModal = false;
  selectedUser: UserListDto | null = null;
  selectedRoleIds: Set<string> = new Set();
  savingRoles = false;

  // Modal de crear/editar usuario
  showUserModal = false;
  isEditMode = false;
  editingUserId: string | null = null;
  userForm = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    roleIds: [] as string[]
  };
  savingUser = false;
  showPassword = false;

  // Modal de reiniciar contraseña
  showPasswordModal = false;
  passwordUserId: string | null = null;
  passwordUserName: string = '';
  newPassword: string = '';
  savingPassword = false;

  constructor(
    private userRolesService: UserRolesService,
    private rolesService: RolesService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadRoles();
    this.loadUsers();
  }

  loadRoles(): void {
    this.rolesService.getAllRoles().subscribe({
      next: (roles) => {
        this.allRoles = roles;
      },
      error: (error) => {
        console.error('Error loading roles:', error);
      }
    });
  }

  loadUsers(): void {
    this.loading = true;
    
    const filter: UserFilterDto = {
      page: this.currentPage,
      pageSize: this.pageSize,
      search: this.searchQuery || undefined,
      roleId: this.selectedRoleFilter || undefined
    };

    this.userRolesService.getUsersWithRoles(filter).subscribe({
      next: (result: PagedResult<UserListDto>) => {
        this.users = result.data;
        this.totalCount = result.totalCount;
        this.totalPages = result.totalPages;
        this.loading = false;
      },
      error: (error) => {
        this.toastService.showToast('error', 'Error al cargar los usuarios');
        this.loading = false;
        console.error('Error loading users:', error);
      }
    });
  }

  search(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadUsers();
  }

  filterByRole(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadUsers();
    }
  }

  openRoleModal(user: UserListDto): void {
    this.selectedUser = user;
    this.selectedRoleIds = new Set();
    
    // Cargar roles actuales del usuario
    this.userRolesService.getUserWithRoles(user.id).subscribe({
      next: (userWithRoles) => {
        userWithRoles.roles.forEach(role => this.selectedRoleIds.add(role.id));
        this.showRoleModal = true;
      },
      error: (error) => {
        this.toastService.showToast('error', 'Error al cargar los roles del usuario');
        console.error('Error loading user roles:', error);
      }
    });
  }

  closeRoleModal(): void {
    this.showRoleModal = false;
    this.selectedUser = null;
    this.selectedRoleIds = new Set();
  }

  toggleRole(roleId: string): void {
    if (this.selectedRoleIds.has(roleId)) {
      this.selectedRoleIds.delete(roleId);
    } else {
      this.selectedRoleIds.add(roleId);
    }
  }

  isRoleSelected(roleId: string): boolean {
    return this.selectedRoleIds.has(roleId);
  }

  saveUserRoles(): void {
    if (!this.selectedUser) return;

    this.savingRoles = true;
    const roleIds = Array.from(this.selectedRoleIds);

    this.userRolesService.assignRolesToUser(this.selectedUser.id, roleIds).subscribe({
      next: () => {
        this.toastService.showToast('success', 'Roles asignados exitosamente');
        this.closeRoleModal();
        this.loadUsers();
      },
      error: (error) => {
        this.toastService.showToast('error', 'Error al asignar roles');
        console.error('Error assigning roles:', error);
      },
      complete: () => {
        this.savingRoles = false;
      }
    });
  }

  getRoleBadgeClass(roleName: string): string {
    switch (roleName) {
      case 'SuperAdmin':
        return 'bg-red-100 text-red-800';
      case 'Admin':
        return 'bg-blue-100 text-blue-800';
      case 'User':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getPages(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.totalPages, this.currentPage + 2);
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  getMaxShown(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  // ==================== CREAR/EDITAR USUARIO ====================

  openCreateUserModal(): void {
    this.isEditMode = false;
    this.editingUserId = null;
    this.userForm = {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      roleIds: []
    };
    this.showUserModal = true;
  }

  openEditUserModal(user: UserListDto): void {
    this.isEditMode = true;
    this.editingUserId = user.id;
    this.userForm = {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: '',
      roleIds: []
    };
    
    // Cargar roles del usuario
    this.userRolesService.getUserWithRoles(user.id).subscribe({
      next: (userData) => {
        this.userForm.roleIds = userData.roles.map(r => r.id);
        this.showUserModal = true;
      },
      error: () => {
        this.showUserModal = true;
      }
    });
  }

  closeUserModal(): void {
    this.showUserModal = false;
    this.editingUserId = null;
    this.showPassword = false;
    this.userForm = {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      roleIds: []
    };
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleUserRole(roleId: string): void {
    const index = this.userForm.roleIds.indexOf(roleId);
    if (index === -1) {
      this.userForm.roleIds.push(roleId);
    } else {
      this.userForm.roleIds.splice(index, 1);
    }
  }

  isUserRoleSelected(roleId: string): boolean {
    return this.userForm.roleIds.includes(roleId);
  }

  saveUser(): void {
    if (!this.userForm.firstName || !this.userForm.lastName || !this.userForm.email) {
      this.toastService.showToast('warning', 'Por favor completa todos los campos requeridos');
      return;
    }

    if (!this.isEditMode && !this.userForm.password) {
      this.toastService.showToast('warning', 'La contraseña es requerida para crear un usuario');
      return;
    }

    this.savingUser = true;

    if (this.isEditMode && this.editingUserId) {
      // Actualizar usuario
      const updateDto: UpdateUserDto = {
        firstName: this.userForm.firstName,
        lastName: this.userForm.lastName,
        email: this.userForm.email
      };

      this.userRolesService.updateUser(this.editingUserId, updateDto).subscribe({
        next: () => {
          // También actualizar roles
          this.userRolesService.assignRolesToUser(this.editingUserId!, this.userForm.roleIds).subscribe({
            next: () => {
              this.toastService.showToast('success', 'Usuario actualizado exitosamente');
              this.closeUserModal();
              this.loadUsers();
            },
            error: () => {
              this.toastService.showToast('warning', 'Usuario actualizado, pero hubo un error al asignar roles');
              this.closeUserModal();
              this.loadUsers();
            },
            complete: () => {
              this.savingUser = false;
            }
          });
        },
        error: (error) => {
          this.toastService.showToast('error', 'Error al actualizar el usuario');
          console.error('Error updating user:', error);
          this.savingUser = false;
        }
      });
    } else {
      // Crear nuevo usuario
      const createDto: CreateUserDto = {
        email: this.userForm.email,
        firstName: this.userForm.firstName,
        lastName: this.userForm.lastName,
        password: this.userForm.password,
        roleIds: this.userForm.roleIds
      };

      this.userRolesService.createUser(createDto).subscribe({
        next: () => {
          this.toastService.showToast('success', 'Usuario creado exitosamente');
          this.closeUserModal();
          this.loadUsers();
        },
        error: (error) => {
          this.toastService.showToast('error', 'Error al crear el usuario. El email podría estar en uso.');
          console.error('Error creating user:', error);
          this.savingUser = false;
        }
      });
    }
  }

  // ==================== REINICIAR CONTRASEÑA ====================

  openPasswordModal(user: UserListDto): void {
    this.passwordUserId = user.id;
    this.passwordUserName = user.fullName;
    this.newPassword = '';
    this.showPasswordModal = true;
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
    this.passwordUserId = null;
    this.passwordUserName = '';
    this.newPassword = '';
  }

  generateRandomPassword(): void {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.newPassword = password;
  }

  savePassword(): void {
    if (!this.passwordUserId || !this.newPassword) {
      this.toastService.showToast('warning', 'La nueva contraseña es requerida');
      return;
    }

    if (this.newPassword.length < 6) {
      this.toastService.showToast('warning', 'La contraseña debe tener al menos 6 caracteres');
      return;
    }

    this.savingPassword = true;

    this.userRolesService.resetUserPassword(this.passwordUserId, { newPassword: this.newPassword }).subscribe({
      next: () => {
        this.toastService.showToast('success', 'Contraseña reiniciada exitosamente');
        this.closePasswordModal();
      },
      error: (error) => {
        this.toastService.showToast('error', 'Error al reiniciar la contraseña');
        console.error('Error resetting password:', error);
      },
      complete: () => {
        this.savingPassword = false;
      }
    });
  }

  // ==================== ACTIVAR / DESACTIVAR USUARIO ====================

  async toggleUserActive(user: UserListDto): Promise<void> {
    const newState = !user.isActive;
    const actionLabel = newState ? 'activar' : 'desactivar';
    const confirmMsg = newState
      ? `¿Activar al usuario "${user.fullName}" (${user.email})? Podrá iniciar sesión nuevamente.`
      : `¿Desactivar al usuario "${user.fullName}" (${user.email})? No podrá iniciar sesión hasta que lo reactives.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    this.userRolesService.setUserActive(user.id, newState).subscribe({
      next: () => {
        this.toastService.showToast('success', newState ? 'Usuario activado exitosamente' : 'Usuario desactivado exitosamente');
        this.loadUsers();
      },
      error: (error) => {
        const msg = error?.error?.message || `Error al ${actionLabel} el usuario`;
        this.toastService.showToast('error', msg);
        console.error('Error toggling user active:', error);
      }
    });
  }
}

